/**
 * RISKS-ADD.ROUTE
 * Add new risks for one unit
 */
import NavFocus from "@/nav/nav.focus"
import PageHeader from "@/components/page-header"
import { t } from "i18next"
import { useNavigate, useLocation } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import unitStore from "@/features/risks/entities/unit/model/unit.store"
import Button from "@/components/button"
import {
  faArrowLeft,
  faPencilAlt,
  faSearch,
  faWandMagicSparkles,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons"
import { useEffect, useState } from "react"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import { RisksAddFromModel } from "../entities/risk/ui/RisksAddFromModel"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { RisksAddFromAssistant } from "../entities/risk/ui/RisksAddFromAssistant"
import { resetStoresSearch } from "../utils/resetStoresSearch"
import Card from "@/components/card"
import Space from "@/components/space"
import { createEmptyRisk, Risk, RiskAddMode } from "../entities/risk/model/risk.types"
import LoadingModal from "@/modals/loading.modal"
import { RisksAddFromJobs } from "../entities/risk/ui/RisksAddFromJobs"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"
import RiskTypeChip from "@/features/risks/entities/risk/ui/RiskTypeChip"
import UnitDescriptionModal from "../entities/unit/ui/modals/UnitDescriptionModal"

export default function RisksAddRoute() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  //Selectors
  const status = useAppSelector(riskStore.selectors.status)
  const unit = useAppSelector(unitStore.selectors.draft)
  const units = useAppSelector(unitStore.selectors.listSelected)
  const organization = useAppSelector(organizationStore.selectors.active)

  //list of cards
  const modeCards: { id: RiskAddMode; text: string; icon: IconDefinition }[] = [
    {
      id: "assistant",
      text: "Laisser l'assistant faire des propositions",
      icon: faWandMagicSparkles
    },
    { id: "model", text: "Rechercher depuis d'autres unités", icon: faSearch },
    { id: "blank", text: "Partir d'une feuille blanche", icon: faPencilAlt }
  ]

  //List of association
  const [associatedRisks] = useState(initAssociatedRisks())

  //State
  const [mode, setMode] = useState<RiskAddMode | null>(null)
  const [riskType, setRiskType] = useState<"phy" | "rps">("phy")
  const [showDescriptionModal, setShowDescriptionModal] = useState(false)
  const [pendingMode, setPendingMode] = useState<RiskAddMode | null>(null)

  const riskTypeLabel = riskType === "rps" ? "psychosociaux" : "physiques"
  const title = `${t("Créer des risques")} ${riskTypeLabel} ${units.length <= 1 ? `pour l'unité « ${unit.name} »` : ""}`
  const subtitle = !mode
    ? units.length <= 1
      ? `Comment souhaitez-vous ajouter des risques ${riskTypeLabel} ?`
      : t("units") + " : " + units.map((x) => x.name).join(", ") + "."
    : undefined

  useEffect(() => {
    resetStoresSearch(dispatch, [riskStore, unitStore])
  }, [dispatch])

  useEffect(() => {
    dispatch(riskStore.actions.clearSelected())
    // Load all risks to have access to all families in the dropdown
    dispatch(riskStore.thunks.fetchValidatedAndSet(null))
  }, [dispatch])

  function initAssociatedRisks() {
    const items: Risk[] = []
    if (units.length > 1) {
      for (let i = 0; i < units.length; i++) {
        const u = units[i]
        if (u.id !== unit.id) {
          items.push(createEmptyRisk({ UnitId: u.id, unitName: u.name }))
        }
      }
    }
    return items
  }

  async function handleModeSelect(nextMode: RiskAddMode) {
    // Si mode assistant et pas de description, afficher la modal
    if (nextMode === "assistant" && !unit.description?.trim()) {
      setPendingMode(nextMode)
      setShowDescriptionModal(true)
      return
    }

    continueModeSelect(nextMode)
  }

  async function continueModeSelect(nextMode: RiskAddMode) {
    setMode(nextMode)

    //Init new risk
    const newRisk = createEmptyRisk()
    newRisk.UnitId = unit.id
    newRisk.rps = riskType === "rps"
    newRisk.attributes[organization.riskAttributes[0]] = "Nouveau risque"

    //Save associations
    newRisk.associatedRisks = associatedRisks

    if (nextMode === "blank") {
      const createdRisk = await dispatch(riskStore.thunks.createEntity(newRisk)).unwrap()
      navigate(`/risks/risk/${createdRisk.id}`)
    } else if (nextMode === "assistant") {
      dispatch(riskStore.actions.setList([]))
      dispatch(riskStore.actions.clearSelected())
      try {
        const suggestedRisks = await dispatch(
          riskStore.thunks.suggestForUnit({
            ...unit,
            rps: riskType === "rps"
          })
        ).unwrap()
        dispatch(
          riskStore.actions.setList(
            suggestedRisks.map((x) => ({
              ...x,
              associatedRisks
            }))
          )
        )
      } catch (err) {
        console.error("❌ Erreur lors de la suggestion de risques :", err)
      }
    }
  }

  const { backTo } = (location.state as any) || {}

  const handleBack = () => {
    navigate(backTo || -1)
  }

  const navFocusText = backTo === "/risks/units" ? t("units") : t("Fiches de risques")

  return (
    <NavFocus text={navFocusText} onBack={handleBack}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        extraBelowSubtitle={
          mode && (
            <div
              className="-ml-10 flex items-center grey-t w-fit z-50 cursor-pointer"
              onClick={() => {
                setMode(null)
                dispatch(riskStore.actions.setList([]))
              }}>
              <div className="page-navigation-back">
                <FontAwesomeIcon icon={faArrowLeft} />
              </div>
              <span className="_hover">{t("Retour à la sélection")}</span>
            </div>
          )
        }
      />

      {mode === "blank" && status === "STATUS_SAVING" && <LoadingModal />}

      {showDescriptionModal && (
        <UnitDescriptionModal
          onClose={() => {
            setShowDescriptionModal(false)
            setPendingMode(null)
          }}
          onContinue={() => {
            setShowDescriptionModal(false)
            if (pendingMode) {
              continueModeSelect(pendingMode)
              setPendingMode(null)
            }
          }}
        />
      )}

      {!mode && (
        <>
          <div className="mt-6 flex items-center gap-3 mb-8">
            <span className="text-sm font-medium">{t("Type de risque")}:</span>
            <div className="flex items-center gap-2 cursor-pointer">
              <RiskTypeChip
                isRps={false}
                showCount={false}
                showLabel={true}
                disableTooltip
                color={"#999"}
                isOutlineColored={riskType === "rps"}
                onClick={() => setRiskType("phy")}
              />
              <RiskTypeChip
                isRps={true}
                showCount={false}
                showLabel={true}
                disableTooltip
                color={"#999"}
                isOutlineColored={riskType !== "rps"}
                onClick={() => setRiskType("rps")}
              />
            </div>
          </div>

          <div className="flex gap-6">
            <img
              src={require("@/assets/owl.png")}
              alt="owl"
              style={{ width: 180, height: "auto", transform: "scale(-1, 1)" }}
            />

            {modeCards.map((card, i) => (
              <Card className="flex1" key={card.id}>
                <div className="flex flex-col justify-around h-[176px]">
                  <div className="text-center text-xl">
                    <div>
                      {card.text} <span className="text-sm grey-t">({riskType.toUpperCase()})</span>
                    </div>
                  </div>
                  <div className="flex">
                    <Space />
                    <Button
                      className={i === 0 ? "primary" : "secondary"}
                      isFullWidth
                      isWithBorder
                      onClick={() => handleModeSelect(card.id)}
                      icon={card.icon}>
                      {t("Choisir")}
                    </Button>
                    <Space />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {unit.JobIds.length > 0 && mode !== "model" && mode !== "assistant" && units.length <= 1 && <RisksAddFromJobs />}
      {mode === "model" && <RisksAddFromModel associatedRisks={associatedRisks} riskType={riskType} />}
      {mode === "assistant" && <RisksAddFromAssistant riskType={riskType} loading={status === "STATUS_LOADING"} />}
    </NavFocus>
  )
}
